import { useCallback } from "react";
import { useDataProvider, useGetIdentity, useTranslate } from "ra-core";

import { getTranslatedCompanySizeLabel } from "../companies/getTranslatedCompanySizeLabel";
import { mapSizeToCategory, sizes } from "../companies/sizes";
import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  findOption,
  toChoice,
  toConfiguredValues,
  toDay,
} from "../spreadsheet/cells";
import { readCustomFields } from "../spreadsheet/customFieldColumns";
import { createEachRow } from "./createEachRow";
import { toNumber, toText } from "./parseCell";
import type { ImportCell, ProcessImportBatch } from "./types";

/**
 * Creates a company per row of an Excel or CSV file. Unknown columns are
 * ignored, missing ones are left empty — except `name`, which the database
 * requires. List columns take the label shown in the CRM or the stored value.
 */
export function useCompanyImport(): ProcessImportBatch {
  const {
    acquisitionChannels,
    companySectors,
    customerStatuses,
    eventTypes,
    prospectStates,
    customFields,
  } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  return useCallback(
    (batch) => {
      const sizeOptions = sizes.map((size) => ({
        value: String(size.id),
        label: getTranslatedCompanySizeLabel(size, translate),
      }));
      return createEachRow(
        batch.map((row) => {
          const eventTypeValues = toConfiguredValues(
            row.event_types,
            eventTypes,
          );
          return dataProvider.create("companies", {
            data: {
              name: toText(row.name),
              customer_status: toChoice(row.customer_status, customerStatuses),
              prospect_state: toChoice(row.prospect_state, prospectStates),
              lead_score: toLeadScore(row.lead_score),
              sector: toChoice(row.sector, companySectors),
              size: sizeOf(row.size, sizeOptions),
              tax_identifier: toText(row.tax_identifier),
              description: toText(row.description),
              event_types: eventTypeValues.length ? eventTypeValues : undefined,
              acquisition_channel: toChoice(
                row.acquisition_channel,
                acquisitionChannels,
              ),
              budget: toNumber(row.budget),
              distance_km: toNumber(row.distance_km),
              last_contact_date: toDay(row.last_contact_date),
              email: toText(row.email),
              phone_number: toText(row.phone_number),
              website: toText(row.website),
              linkedin_url: toText(row.linkedin_url),
              address: toText(row.address),
              zipcode: toText(row.zipcode),
              city: toText(row.city),
              state_abbr: toText(row.state_abbr),
              country: toText(row.country),
              revenue: toText(row.revenue),
              custom_fields: readCustomFields(
                row,
                customFields.companies ?? [],
              ),
              sales_id: identity?.id,
              created_at: new Date().toISOString(),
            },
          });
        }),
      );
    },
    [
      acquisitionChannels,
      companySectors,
      customerStatuses,
      customFields,
      dataProvider,
      eventTypes,
      identity?.id,
      prospectStates,
      translate,
    ],
  );
}

/** A lead score from 1 to 5; anything else is left empty. */
export const toLeadScore = (cell: ImportCell): number | undefined => {
  const score = toNumber(cell);
  return score !== undefined &&
    Number.isInteger(score) &&
    score >= 1 &&
    score <= 5
    ? score
    : undefined;
};

/**
 * `size` is a bucket id, not a headcount: the label of a bucket (« 10 à 49
 * employés ») picks it, an arbitrary headcount is coerced into the nearest one.
 */
const sizeOf = (
  cell: ImportCell,
  options: { value: string; label: string }[],
) => {
  const headcount = toNumber(cell);
  if (headcount !== undefined) return mapSizeToCategory(headcount);
  const text = toText(cell);
  const option = text === undefined ? undefined : findOption(text, options);
  return option ? Number(option.value) : undefined;
};
